/**
 * The one list of public, indexable pages, and the copy each one is served with.
 *
 * CommonJS on purpose. It is imported by the Create React App bundle (webpack
 * reads module.exports fine) and loaded by scripts/prerender.mjs through
 * createRequire on Node 16, so the HTML a crawler is served and the page React
 * renders read their title, description and heading from the same place. Do not
 * convert this file to ESM and do not add "type": "module" to package.json, or
 * the prerender step stops being able to require it.
 *
 * Following the BostonHomeTask rule: a URL is in the sitemap only when it answers
 * one search that no other URL answers, and that answer is in the served HTML.
 * Everything behind a login is noindex and left out of the sitemap.
 */

// The production origin. Canonicals and the sitemap are always written against
// this host, never the QA host, so QA cannot compete for the same URLs.
const SITE_URL = "https://www.mermadicrunner.com";

// Shown for any route that is not one of the indexable pages below - the app
// screens, which are noindex, so none of them keeps the last marketing title.
const DEFAULT_TITLE = "Mermadic Solutions";
const DEFAULT_DESCRIPTION =
  "Managed dedicated cloud for batch jobs, long-running APIs, CI/CD automation, " +
  "and private container images.";

// The indexable set. `article` entries are rendered by src/pages/SeoArticle.js
// and written as a body by the prerender; the rest already have a React page and
// only take a title, description and canonical.
const PAGES = {
  "/": {
    title: "Dedicated Kubernetes cluster, no AWS account | Mermadic Solutions",
    description:
      "Mermadic gives you a dedicated, fully managed Kubernetes cluster in its own " +
      "private VPC - no AWS account required. Run Docker batch jobs billed per second, " +
      "long-running services, CI/CD automation, and a private container registry, all on one bill.",
    h1: "Run jobs. Deploy services. Ship faster - fully managed, fully isolated.",
  },

  "/pricing": {
    title: "Dedicated Kubernetes pricing - Shared, Gold, and Premium | Mermadic Solutions",
    description:
      "Mermadic pricing: start free on Shared and pay only for what you run, or get a " +
      "dedicated multi-AZ cluster on Gold or Premium. Jobs billed per second, optional GPU " +
      "on Premium, one bill with no egress surprises.",
    h1: "Simple, transparent pricing",
  },

  "/documentation": {
    title:
      "Documentation - jobs, deployments, registry, and the dedicated API | Mermadic Solutions",
    description:
      "How to use Mermadic: create a cluster, push images to the container registry, run " +
      "jobs and cron, deploy long-running services, set custom domains, and automate with " +
      "CI/CD through your cluster's dedicated API.",
    h1: "Mermadic documentation",
  },

  "/kubernetes-without-aws": {
    title: "Run Kubernetes without an AWS account | Mermadic Solutions",
    description:
      "Get a dedicated, fully managed Kubernetes cluster without opening an AWS account, " +
      "writing IAM policies, or designing a VPC. Mermadic provisions and operates the cluster; " +
      "you ship containers.",
    h1: "Run Kubernetes without an AWS account",
    article: [
      "Most managed Kubernetes still assumes you bring your own cloud account: an AWS " +
        "organization to create and fund, IAM roles and trust policies to configure, and a VPC " +
        "to design before you can run a single container. Mermadic removes that step. There is " +
        "no AWS account to open and no cloud networking to learn.",
      "Every Mermadic account runs on isolated infrastructure - your own private AWS VPC, your " +
        "own cluster on your own nodes, and a dedicated control API per cluster. Workloads are " +
        "separated at the network layer, not by namespace inside a shared cluster, so no other " +
        "tenant's traffic ever reaches your subnets.",
      "Mermadic provisions, upgrades, scales, and monitors the cluster for you, and bills it all " +
        "on one invoice with no separate cloud account to reconcile. You create a cluster, push " +
        "an image, and run a job or deploy a service.",
    ],
  },

  "/batch-jobs": {
    title: "Run Docker batch jobs through one API call | Mermadic Solutions",
    description:
      "Submit any Docker container as a one-off or scheduled job through a single API call. " +
      "Parallelize batch workloads and data processing at any scale, billed per second with no " +
      "idle cost.",
    h1: "Run Docker batch jobs through one API call",
    article: [
      "A Mermadic job is any Docker container, submitted through a single API call and run on " +
        "your dedicated cluster. Point it at an image in the Mermadic Container Registry or any " +
        "public image, set the CPU and memory it needs, and it runs.",
      "Jobs are billed per second, so you never pay for idle capacity, and job-only nodes scale " +
        "to zero when nothing is running. Parallelize batch workloads, ETL pipelines, and data " +
        "processing across many jobs at once, or schedule them with cron when they need to run " +
        "on a timetable.",
      "Because the cluster and its API are dedicated to your account, there are no shared request " +
        "queues and no cross-tenant rate limits on the jobs you submit.",
    ],
  },

  "/gpu-jobs": {
    title: "GPU batch jobs, billed by the second | Mermadic Solutions",
    description:
      "Run GPU jobs on NVIDIA T4 nodes on the Premium tier. GPU capacity is usage-billed and " +
      "scales to zero when idle, so you only pay while a job runs.",
    h1: "GPU batch jobs, billed by the second",
    article: [
      "Some jobs need a GPU. On the Premium tier, a job can request an NVIDIA T4 node, submitted " +
        "through the same job API as any other container - you add a GPU flag and the job lands " +
        "on GPU hardware.",
      "GPU capacity is usage-billed and scales to zero when idle, so there is no always-on GPU " +
        "cost between runs. A GPU job has a whole node to itself, so its CPU and memory are " +
        "included in the GPU rate rather than billed on top.",
      "GPU jobs run on your own dedicated cluster inside your isolated VPC, the same as every " +
        "other workload, and appear on the same single invoice.",
    ],
  },

  "/contact": {
    title: "Contact Mermadic Solutions",
    description:
      "Questions about pricing, the dedicated API, or the container registry? Send the Mermadic " +
      "team a message and we'll get back to you.",
    h1: "We'd love to hear from you",
  },

  "/terms": {
    title: "Terms of Service | Mermadic Solutions",
    description:
      "The terms for using Mermadic - what we provide, what we charge, and what we each agree to.",
    h1: "Terms of Service",
  },

  "/privacy": {
    title: "Privacy Policy | Mermadic Solutions",
    description:
      "What Mermadic collects when you use the service, why we collect it, and how you stay in " +
      "control of it.",
    h1: "Privacy Policy",
  },
};

// Rendered as a standalone article by src/pages/SeoArticle.js. The prerender
// writes the same heading and paragraphs into the served HTML.
const ARTICLE_PATHS = ["/kubernetes-without-aws", "/batch-jobs", "/gpu-jobs"];

// Routes that need a session or are auth flows. Never indexed, never in the
// sitemap, and disallowed in the production robots.txt. Matched as a prefix so
// /deployment/secrets and /mcr/registry are covered by the base entry.
const NOINDEX_PREFIXES = [
  "/login",
  "/signup",
  "/verification",
  "/forgot-password",
  "/github/callback",
  "/create-job",
  "/log",
  "/cron",
  "/cluster",
  "/billing",
  "/account",
  "/deployment",
  "/mcr",
  "/cdci",
  "/health",
  "/not-authorized",
  "/resetpassword",
];

// Drop a trailing slash (except on "/") so "/pricing" and "/pricing/" resolve to
// the same entry and the same canonical.
function normalizePath(path) {
  if (!path) return "/";
  return path !== "/" && path.endsWith("/") ? path.slice(0, -1) : path;
}

// The indexable page for a path, or null. The canonical path is the key itself.
function pageForPath(path) {
  return PAGES[normalizePath(path)] || null;
}

function isNoindexPath(path) {
  const p = normalizePath(path);
  return NOINDEX_PREFIXES.some((prefix) => p === prefix || p.startsWith(prefix + "/"));
}

module.exports = {
  SITE_URL,
  DEFAULT_TITLE,
  DEFAULT_DESCRIPTION,
  PAGES,
  ARTICLE_PATHS,
  NOINDEX_PREFIXES,
  normalizePath,
  pageForPath,
  isNoindexPath,
};
